import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    EditButton,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    ReferenceInput,
    SelectInput,
    FunctionField,
    required,
    useRecordContext,
} from 'react-admin';
import { Alert, Box, Chip } from '@mui/material';
import { FormToolbar } from '../../common/FormToolbar';
import { isSchoolAdmin } from '../../common/schoolRoles';

/**
 * Class sections -- "Class 11, Section A".
 *
 * The unit of teaching AND the unit of authorisation: a teacher's access is a
 * set of section ids, and a student belongs to exactly one section.
 *
 * WHAT A TEACHER SEES HERE
 * ------------------------
 * Only the sections they are assigned to. That filtering happens in the API,
 * not here -- this screen simply renders what comes back. Worth stating,
 * because the natural assumption on reading a list component is that it shows
 * the table; this one shows the caller's slice of it, and a teacher with no
 * assignment gets an empty page.
 *
 * Writing is administrator-only, so the create and edit buttons are hidden
 * from teachers. The API refuses them regardless -- this only keeps a teacher
 * from finding a form that will reject them.
 */

const SectionList = props => {
    const admin = isSchoolAdmin();

    const filters = [
        <TextInput source="q" label="Search" alwaysOn resettable />,
        <ReferenceInput source="academic_year_id" reference="academic-year" label="Year">
            <SelectInput optionText="name" />
        </ReferenceInput>,
        <TextInput source="stream" label="Stream" />,
    ];

    return (
        <List
            title="Class sections"
            {...props}
            filters={admin ? filters : [<TextInput source="q" label="Search" alwaysOn resettable />]}
            sort={{ field: 'class_name', order: 'ASC' }}
            hasCreate={admin}
            exporter={false}
            empty={
                <Alert severity="info" sx={{ m: 3 }}>
                    {admin
                        ? 'No class sections yet. Create an academic year first, then add sections to it.'
                        : 'You have not been assigned to any class section yet. Ask your school administrator to assign you one.'}
                </Alert>
            }
        >
            <Datagrid rowClick={admin ? 'edit' : false} bulkActionButtons={false}>
                {admin && <EditButton />}
                <FunctionField
                    label="Section"
                    sortBy="class_name"
                    render={(r: any) => <b>{r.label}</b>}
                />
                <TextField source="stream" label="Stream" />
                <TextField source="academic_year_name" label="Year" />
                <FunctionField
                    label="Current"
                    render={(r: any) =>
                        r.is_current ? <Chip size="small" label="current" color="success" /> : null
                    }
                />
                <NumberField source="student_count" label="Students" />
                <NumberField source="teacher_count" label="Teachers" />
            </Datagrid>
        </List>
    );
};

const SectionForm = () => (
    <>
        <ReferenceInput source="academic_year_id" reference="academic-year" label="Academic year">
            <SelectInput optionText="name" validate={[required()]} sx={{ minWidth: 220 }} />
        </ReferenceInput>

        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <TextInput
                source="class_name"
                label="Class"
                validate={[required()]}
                helperText="For example 11"
                sx={{ flex: 1 }}
            />
            <TextInput
                source="section_name"
                label="Section"
                validate={[required()]}
                helperText="For example A"
                sx={{ flex: 1 }}
            />
            <TextInput
                source="stream"
                label="Stream"
                helperText="Science / Commerce / Arts. Optional."
                sx={{ flex: 1 }}
            />
        </Box>
    </>
);

const SectionCreate = props => (
    <Create {...props} title="New class section" redirect="list">
        <SimpleForm toolbar={<FormToolbar {...props} />}>
            <SectionForm />
        </SimpleForm>
    </Create>
);

/**
 * Deleting is offered but the API refuses it while students are in the
 * section, with a message saying to move them first. teacher_assignment rows
 * cascade, which correctly NARROWS those teachers' access -- the safe
 * direction, and the reason a section with only teachers attached can go.
 */
const SectionEdit = props => (
    <Edit {...props} title="Class section" mutationMode="pessimistic">
        <SimpleForm toolbar={<FormToolbar {...props} hasDelete />}>
            <StudentWarning />
            <SectionForm />
        </SimpleForm>
    </Edit>
);

const StudentWarning = () => {
    const record = useRecordContext();
    if (!record || !record.student_count) return null;
    return (
        <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
            {record.student_count} student(s) are in this section. It cannot be deleted until they
            are moved somewhere else.
        </Alert>
    );
};

export default { list: SectionList, create: SectionCreate, edit: SectionEdit };
